import React, { type ComponentProps, useLayoutEffect } from 'react';
import { type Href, useNavigation } from 'expo-router';
import type { ViewProps } from 'react-native';
import { Platform, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';

import { Aside } from '@expo/html-elements';
import { theme } from '@garnish/constants';
import { Container, useResponsive } from '@garnish/core';
import { useNavbarContainerHeight } from '@garnish/navigation';

import { useIsLoggedIn, useIsLoggingIn } from '../../../context/AuthMachine';
import { LoadingAnimation } from '../../LoadingAnimation';
import { PageWrapper } from '../../PageWrapper';
import { AccountMenu } from '../AccountMenu';
import { AccountSignedOutMessage } from '../AccountSignedOutMessage';

import type { AccountTabScreenId } from '../Account.types';

// ────────────────────────────────────────────────────────────────────────────────

export const AccountScreenContainer = (props: AccountScreenContainerProps) => {
  const { headerShown, redirectTo, screen: screenId } = props;

  const { currentBreakpoint } = useResponsive();
  const isLoggedIn = useIsLoggedIn();
  const isLoggingIn = useIsLoggingIn();
  const navigation = useNavigation();
  const setOptions = navigation.setOptions;

  // ─── Effects ─────────────────────────────────────────────────────────

  useLayoutEffect(() => {
    setOptions({ headerShown: headerShown ?? false });
  }, [setOptions, headerShown]);

  // ─────────────────────────────────────────────────────────────────

  if (isLoggingIn) {
    return <LoadingAnimation />;
  }

  if (!isLoggedIn) {
    return <AccountSignedOutMessage redirectTo={redirectTo ?? getAccountScreenHref(screenId)} />;
  }

  if (currentBreakpoint.isXS) {
    return <AccountScreenContainerXS {...props} />;
  }

  if (Platform.OS !== 'web') {
    return <AccountScreenContainerNativeSM {...props} />;
  }

  return <AccountScreenContainerSM {...props} />;
};

// ─── Sub Components ──────────────────────────────────────────────────────────

const AccountScreenContainerPageWrapper = (
  props: Pick<AccountScreenContainerProps, 'children' | 'testID'>,
) => {
  const { testID, children } = props;
  const { currentBreakpoint } = useResponsive();

  return (
    <PageWrapper
      testID={testID}
      showsVerticalScrollIndicator={false}
      safeAreaEdges={ACCOUNT_SAFE_AREA_EDGES}
      safeAreaViewStyle={accountSafeAreaViewStyle}
      style={styles.pageWrapper}
      withHeaderTitle
      withoutFooter={currentBreakpoint.isXS}
    >
      {children}
    </PageWrapper>
  );
};

const AccountScreenContainerSM = (props: AccountScreenContainerProps) => {
  const { screen: screenId, testID, children, dataSet } = props;

  const { minWidth, match } = useResponsive();

  const { height: navbarHeight, paddingTopStyle: navbarPaddingTopStyle } =
    useNavbarContainerHeight();

  const tabContentNavbarPaddingStyle = useStyle(
    () => ({ paddingTop: navbarHeight + theme.spacing['12'] }),
    [navbarHeight],
  );

  // ─── Flags ───────────────────────────────────────────────────────────

  // ─── Styles ──────────────────────────────────────────────────────────

  const menuWrapperStyles = [
    styles.menuWrapper,
    match([{}, styles.menuWrapperSM, styles.menuWrapperMD]),
    navbarPaddingTopStyle,
  ];

  const tabContentStyles = [
    styles.tabContent,
    minWidth.isLG ? styles.tabContentLG : {},
    tabContentNavbarPaddingStyle,
  ];

  return (
    <AccountScreenContainerPageWrapper testID={testID}>
      <Container customSpacing={0} wrapperStyle={styles.containerSM} style={styles.containerSM}>
        <View style={styles.contentWrapper}>
          <Aside style={menuWrapperStyles}>
            <AccountMenu screen={screenId} />
          </Aside>

          <View style={tabContentStyles} {...(dataSet ? { dataSet } : {})}>
            {children}
          </View>
        </View>
      </Container>
    </AccountScreenContainerPageWrapper>
  );
};

/**
 * Native wide layouts render the account menu once, in the Account stack layout
 * (`AccountSidebar`), so each screen only renders its own scrollable content.
 */
const AccountScreenContainerNativeSM = (props: AccountScreenContainerProps) => {
  const { testID, children, dataSet } = props;

  const { minWidth } = useResponsive();
  const { height: navbarHeight } = useNavbarContainerHeight();

  const tabContentNavbarPaddingStyle = useStyle(
    () => ({ paddingTop: navbarHeight + theme.spacing['12'] }),
    [navbarHeight],
  );

  // ─── Styles ──────────────────────────────────────────────────────────

  const tabContentStyles = [
    styles.tabContent,
    styles.tabContentWithBottomSpacingSM,
    minWidth.isLG ? styles.tabContentLG : {},
    tabContentNavbarPaddingStyle,
  ];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <AccountScreenContainerPageWrapper testID={testID}>
      <View style={tabContentStyles} {...(dataSet ? { dataSet } : {})}>
        {children}
      </View>
    </AccountScreenContainerPageWrapper>
  );
};

const AccountScreenContainerXS = (props: AccountScreenContainerProps) => {
  const { testID, children, removeSpacing, dataSet, screen } = props;

  const { paddingBottomStyle: navbarPaddingBottomStyle } = useNavbarContainerHeight();
  const { top: safeAreaTop, right: safeAreaRight, left: safeAreaLeft } = useSafeAreaInsets();

  // ─── Flags ───────────────────────────────────────────────────────────

  const hasRightSafeArea = safeAreaRight > 0;
  const hasLeftSafeArea = safeAreaLeft > 0;

  // ─── Styles ──────────────────────────────────────────────────────────

  const containerDynamicStyles = useStyle(() => {
    if (screen === 'orders') return;

    return {
      paddingTop: Math.max(safeAreaTop + theme.spacing['4'], theme.spacing['6']),
    };
  }, [safeAreaTop, screen]);

  const containerStyles = [
    styles.containerXS,
    containerDynamicStyles,
    navbarPaddingBottomStyle,
    hasRightSafeArea ? styles.containerWithBorderRightXS : undefined,
    hasLeftSafeArea ? styles.containerWithBorderLeftXS : undefined,
  ];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <AccountScreenContainerPageWrapper testID={testID}>
      <Container
        removeSpacing={removeSpacing}
        wrapperStyle={styles.containerXS}
        style={containerStyles}
        {...(dataSet ? { dataSet } : {})}
      >
        {children}
      </Container>
    </AccountScreenContainerPageWrapper>
  );
};

// ────────────────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  pageWrapper: {
    flex: 1,
    backgroundColor: theme.colors.APP_BACKGROUND,
  },
  safeAreaView: {
    maxHeight: undefined, // overrides default style
  },
  contentWrapper: {
    flex: 1,
    flexDirection: 'row',
  },
  containerXS: {
    flex: 1,
    minHeight: '100%',
  },
  containerWithBorderRightXS: {
    borderRightWidth: 1,
    borderRightColor: theme.colors.LIGHT,
  },
  containerWithBorderLeftXS: {
    borderLeftWidth: 1,
    borderLeftColor: theme.colors.LIGHT,
  },
  containerSM: {
    flex: 1,
    minHeight: 700,
  },
  tabContent: {
    flex: 1,
    minHeight: '100%',
    paddingTop: theme.spacing['12'],
    paddingHorizontal: theme.spacing['10'],
  },
  tabContentLG: {
    paddingHorizontal: theme.spacing['20'],
  },
  tabContentWithBottomSpacingSM: {
    paddingBottom: theme.spacing['10'],
  },
  menuWrapper: {
    borderRightWidth: 1,
    borderRightColor: theme.colors.LIGHT,
  },
  menuWrapperSM: {
    width: 280,
  },
  menuWrapperMD: {
    width: 320,
  },
});

const accountSafeAreaViewStyle = Platform.select({ native: styles.safeAreaView });

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getAccountScreenHref(screenId: AccountTabScreenId) {
  // The "profile" tab id maps to the `/account/info` route; everything else
  // matches its tab id 1:1.
  if (screenId === 'profile') return '/account/info';

  return `/account/${screenId}`;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const ACCOUNT_SAFE_AREA_EDGES: PageWrapperSafeAreaEdges = ['right', 'left'];

// ────────────────────────────────────────────────────────────────────────────────

export type AccountScreenContainerProps = Readonly<{
  screen: AccountTabScreenId;
  headerShown?: boolean;
  removeSpacing?: boolean;
  dataSet?: Record<string, string>;
  redirectTo?: Href | string;
}> &
  ViewProps;

type PageWrapperSafeAreaEdges = ComponentProps<typeof PageWrapper>['safeAreaEdges'];
